import { useMemo } from 'react'
import { collection, limit, query, where } from 'firebase/firestore'
import { useCollectionData } from 'react-firebase-hooks/firestore'
import { firebase_firestore } from '../common/firebaseConfig'
import { COMPOSITE_LIMITS, CustomIndicatorDoc } from '../types/composite'

function useCustomIndicators({
  gcid,
  tradingHouse,
  enabled,
}: {
  gcid?: string
  tradingHouse?: string
  enabled: boolean
}) {
  // Top-level collection (owner is a FIELD, not the path) so pattern
  // conditions can reference indicators by id alone — copied/shared patterns
  // keep resolving regardless of who owns the indicator. tradingHouse is
  // filtered server-side too (equality-only queries merge single-field
  // indexes) so the limit window can't fill with another house's docs.
  const [docs, loading, error] = useCollectionData(
    enabled && gcid && tradingHouse
      ? query(
          collection(firebase_firestore, 'customIndicators'),
          where('gcid', '==', gcid),
          where('tradingHouse', '==', tradingHouse),
          // Hard bound on billed reads: 3x the ACTIVE-only publish cap so
          // deactivated docs (no hard delete exists) can't crowd active ones
          // out of the window
          limit(COMPOSITE_LIMITS.maxPerUser * 3)
        )
      : null
  )

  // Memoized on the snapshot: this list feeds the registry-rebuild effect in
  // PatternContext, so a fresh array identity per render would loop it.
  const customIndicators = useMemo(() => {
    const parsed: CustomIndicatorDoc[] = []
    for (const raw of (docs ?? []) as any[]) {
      // Never trust doc shape: one malformed doc must skip, not throw inside
      // the registry-rebuild memo and take down the whole indicator picker
      if (typeof raw?.defJson !== 'string' || typeof raw?.name !== 'string') continue
      if (tradingHouse && raw.tradingHouse !== tradingHouse) continue
      try {
        const def = JSON.parse(raw.defJson)
        if (def?.v === 1 && Array.isArray(def.nodes) && Array.isArray(def.outputs)) {
          parsed.push({ ...raw, def })
        }
      } catch {
        // Malformed defJson: skip rather than break the registry
      }
    }
    return parsed.sort((a, b) => a.name.localeCompare(b.name))
  }, [docs, tradingHouse])

  return { customIndicators, loading, error }
}

export default useCustomIndicators
